import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapPin } from "lucide-react";

import { pickableProducts, productService, type ProductNode } from "@/entities/product";
import { SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";

import {
  listCityGroups,
  listDistricts,
  type CityGroup,
  type DistrictNode,
} from "@/shared/lib/amap/districtService";
import { useMapLead } from "../model/useMapLead";
import { LeadDetailDrawer } from "./components/LeadDetailDrawer";
import { LeadList } from "./components/LeadList";
import { LeadMap } from "./components/LeadMap";
import { MyLeadsTab } from "./components/MyLeadsTab";
import { SearchPanel } from "./components/SearchPanel";

/**
 * 地图获客(获客舱首个自研子能力)。定稿:docs/2026-08-23-map-lead-huoke-cabin-mvp-v1.1.md。
 * 页内两 Tab:找客户(搜索/地图/列表/详情)| 我的线索。无导出、无跟进状态机、评分纯规则分。
 *
 * 行政区联动(P2 正式做法,2026-08-24 用户拍板):城市/区县下拉吃高德 DistrictSearch 真数据,
 * 选中即联动地图(边界描边 + setBounds;周边模式顺手把搜索中心放到所选行政区中心)。
 * 行政区服务任何一步失败 → cityGroups=null → SearchPanel 退回静态城市 + 区域手打,搜索不受影响。
 */
export default function MapLeadPage() {
  const ml = useMapLead();
  // 价签走服务端价目表(现价:搜索 2 分/次、军师分析 2 分/次 —— 0907 起;以服务端价目表为准)。
  // 取不到不编数(book.unit 返 null → 面上退「按次计费」字样),与 GEO 价签同口径。
  const { book } = usePriceBook();
  const searchPrice = book.unit(SKU.mapLeadSearch, "default");
  const analyzePrice = book.unit(SKU.mapLeadAnalyze, "default");
  const [mapReady, setMapReady] = useState(false);
  const onMapReady = useCallback((ready: boolean) => setMapReady(ready), []);

  // ── 行政区联动状态(展示字符串仍走 ml.form,这里只管 adcode/center 元数据) ──
  const [cityGroups, setCityGroups] = useState<CityGroup[] | null | undefined>(undefined);
  // 主推产品下拉数据源(2026-09-02):拉不到(如无读权限)或未建产品库 → null/空数组 →
  // SearchPanel 2026-09-07 起改渲染纯文本输入框(落 productText),不再整块缺席,搜索不受影响。
  const [products, setProducts] = useState<ProductNode[] | null>(null);
  const [districts, setDistricts] = useState<DistrictNode[] | null>(null);
  const [selCity, setSelCity] = useState<DistrictNode | null>(null);
  const [selDistrict, setSelDistrict] = useState<DistrictNode | null>(null);

  useEffect(() => {
    let alive = true;
    listCityGroups().then((groups) => {
      if (alive) setCityGroups(groups);
    });
    productService.list().then(
      (ns) => {
        if (alive) setProducts(pickableProducts(ns));
      },
      () => {
        if (alive) setProducts(null);
      }
    );
    return () => {
      alive = false;
    };
  }, []);

  const cityIndex = useMemo(() => {
    const idx = new Map<string, DistrictNode>();
    for (const g of cityGroups ?? []) for (const c of g.cities) idx.set(c.adcode, c);
    return idx;
  }, [cityGroups]);

  const onSelectCity = useCallback(
    (adcode: string) => {
      const city = cityIndex.get(adcode) ?? null;
      setSelCity(city);
      setSelDistrict(null);
      setDistricts(null);
      ml.patch({ city: city?.name ?? "", district: "" });
      if (city) {
        listDistricts(city.adcode).then((ds) => setDistricts(ds));
      }
    },
    [cityIndex, ml]
  );

  const onSelectDistrict = useCallback(
    (adcode: string) => {
      const d = adcode ? ((districts ?? []).find((x) => x.adcode === adcode) ?? null) : null;
      setSelDistrict(d);
      ml.patch({ district: d?.name ?? "" });
    },
    [districts, ml]
  );

  // 地图焦点 = 区县优先,其次城市。
  const focus = selDistrict ?? selCity;

  // 周边模式:行政区变化时把搜索中心放到其中心(此后用户点图微调不受影响 —— 本效应只跟 focus 走)。
  const focusKey = focus?.adcode ?? "";
  const modeRef = useRef(ml.form.mode);
  modeRef.current = ml.form.mode;
  const setCenter = ml.setCenter;
  useEffect(() => {
    if (!focusKey) return;
    const f = focus;
    if (modeRef.current === "AROUND" && f?.center) {
      setCenter({ lng: f.center.lng, lat: f.center.lat });
    }
    // 依赖刻意只挂 focusKey:mode 切换不重置用户手点的中心。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, setCenter]);

  // 切到周边模式时若还没有中心点,用当前行政区中心垫底(选了北京不至于还要从上海拖图)。
  useEffect(() => {
    if (ml.form.mode === "AROUND" && !ml.center && focus?.center) {
      setCenter({ lng: focus.center.lng, lat: focus.center.lat });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ml.form.mode]);

  return (
    // ★不带自己的 p-*(0917):页面留白由布局壳的 `<main className="flex-1 p-4 md:p-6">` 统一给,
    //   这里再叠一层 = 双重内边距,整页比别的页右移下移一档。
    //   2026-09-01 用户报过一次「获客舱内容没跟其他页面对齐左上角」,当时修了 hub、漏了本页;
    //   获客舱重构后本页降成二级入口、跟今日商机/线索库/信号订阅并排,对不齐更显眼。
    <div className="space-y-4">
      <PageHeader
        backTo="/ai-huoke"
        icon={MapPin}
        title="地图获客"
        subtitle="用地图发现身边潜在客户，AI 帮你筛选高价值商机"
      />

      <Tabs defaultValue="search">
        <TabsList>
          <TabsTrigger value="search">找客户</TabsTrigger>
          <TabsTrigger value="leads">我的线索</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)_280px]">
            <SearchPanel
              form={ml.form}
              patch={ml.patch}
              products={products}
              quota={ml.quota}
              searchPrice={searchPrice}
              loading={ml.loading}
              mapReady={mapReady}
              hasCenter={ml.center != null}
              onSearch={ml.search}
              cityGroups={cityGroups ?? null}
              districts={districts}
              selCityAdcode={selCity?.adcode ?? ""}
              selDistrictAdcode={selDistrict?.adcode ?? ""}
              onSelectCity={onSelectCity}
              onSelectDistrict={onSelectDistrict}
            />
            <div className="min-h-[380px]">
              <LeadMap
                items={ml.visibleItems}
                selectedPoiId={ml.selectedPoiId}
                onSelect={ml.setSelectedPoiId}
                center={ml.center}
                onPickCenter={ml.setCenter}
                radiusMeters={ml.form.radiusKm * 1000}
                aroundMode={ml.form.mode === "AROUND"}
                onReady={onMapReady}
                focus={focus}
              />
            </div>
            <LeadDetailDrawer
              item={ml.selected}
              analyzePrice={analyzePrice}
              joined={ml.selected ? ml.joinedPoiIds.has(ml.selected.poiId) : false}
              onConvert={ml.convertOne}
              analysis={ml.selected ? (ml.analyses.get(ml.selected.poiId) ?? null) : null}
              analyzing={ml.selected ? ml.analyzingPoiId === ml.selected.poiId : false}
              onAnalyze={ml.analyzeOne}
            />
          </div>

          <LeadList
            items={ml.visibleItems}
            hasMore={ml.hasMore}
            page={ml.page}
            loading={ml.loading}
            searched={ml.searched}
            selectedPoiId={ml.selectedPoiId}
            checked={ml.checked}
            joinedPoiIds={ml.joinedPoiIds}
            onSelect={ml.setSelectedPoiId}
            onToggleChecked={ml.toggleChecked}
            onConvertOne={ml.convertOne}
            onConvertChecked={ml.convertChecked}
            onGotoPage={ml.gotoPage}
          />
        </TabsContent>

        <TabsContent value="leads" className="mt-4">
          <MyLeadsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
